import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";

const TONES = {
  neutral: {
    icon: "bg-[#F3EFE7] text-[#4A4640]",
    value: "text-[#111111]",
    hover: "hover:border-[#D8CFC2]",
  },
  orange: {
    icon: "bg-[#FFF0E9] text-[#F75A1B]",
    value: "text-[#111111]",
    hover: "hover:border-[#F75A1B]/35",
  },
  success: {
    icon: "bg-[#E6F4EA] text-[#2F855A]",
    value: "text-[#111111]",
    hover: "hover:border-[#2F855A]/30",
  },
  warning: {
    icon: "bg-[#FEEBC8] text-[#C05621]",
    value: "text-[#111111]",
    hover: "hover:border-[#C05621]/30",
  },
  critical: {
    icon: "bg-[#FED7D7] text-[#C53030]",
    value: "text-[#111111]",
    hover: "hover:border-[#C53030]/30",
  },
};

export function PageHeader({ eyebrow, title, description, meta, action, compact = false }) {
  return (
    <section className={`relative overflow-hidden border border-[#E0D9CD] bg-white ${compact ? "rounded-[20px] p-4 sm:px-5 sm:py-4" : "rounded-[20px] p-4 sm:rounded-[24px] sm:p-7"}`} data-testid="admin-page-header">
      <div className={`pointer-events-none absolute rounded-full bg-[#FFF0E9] ${compact ? "-right-8 -top-9 h-24 w-24 sm:-right-9 sm:-top-10 sm:h-28 sm:w-28" : "-right-9 -top-11 h-28 w-28 sm:-right-14 sm:-top-16 sm:h-44 sm:w-44"}`} />
      <div className={`pointer-events-none absolute hidden rounded-full bg-[#FFF7F2] sm:block ${compact ? "-bottom-16 right-24 h-24 w-24" : "-bottom-20 right-24 h-32 w-32"}`} />
      <div className={`relative flex flex-col sm:flex-row sm:justify-between ${compact ? "gap-3 sm:items-center" : "gap-3 sm:gap-4 sm:items-end"}`}>
        <div className="min-w-0">
          {eyebrow && <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">{eyebrow}</p>}
          <h1 className={`font-display font-bold leading-tight ${compact ? "mt-0.5 text-2xl" : "mt-1 text-[26px] sm:text-3xl"}`}>{title}</h1>
          {description && <p className={`max-w-2xl text-[13px] leading-5 text-[#6B655E] sm:text-sm ${compact ? "mt-1" : "mt-1.5 sm:mt-2"}`}>{description}</p>}
          {meta && <p className={`text-[11px] font-medium text-[#8A8378] sm:text-xs ${compact ? "mt-1.5" : "mt-2 sm:mt-3"}`}>{meta}</p>}
        </div>
        {action && <div className="shrink-0 self-start sm:self-auto">{action}</div>}
      </div>
    </section>
  );
}

export function StatCard({ title, value, hint, icon: Icon, tone = "neutral", testId, onClick, compact = false, showActionLabel = true }) {
  const styles = TONES[tone] || TONES.neutral;
  const className = `w-full border border-[#E0D9CD] bg-white text-left transition duration-150 ${styles.hover} ${compact ? "rounded-[14px] p-4" : "rounded-2xl p-5"} ${onClick ? "hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(68,53,35,0.06)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/35" : ""}`;
  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`${compact ? "text-xs" : "text-sm"} font-medium text-[#6B655E]`}>{title}</p>
          <p className={`${compact ? "mt-1 text-[28px]" : "mt-2 text-3xl"} font-display font-bold tracking-tight ${styles.value}`}>{value}</p>
          {hint && <p className="mt-1 text-[11px] leading-relaxed text-[#8A8378]">{hint}</p>}
        </div>
        {Icon && <span className={`grid shrink-0 place-items-center rounded-xl ${styles.icon} ${compact ? "h-9 w-9" : "h-10 w-10"}`}><Icon className={compact ? "h-[18px] w-[18px]" : "h-5 w-5"} /></span>}
      </div>
      {onClick && showActionLabel && <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#F75A1B]">Ver detalhes <ArrowRight className="h-3.5 w-3.5" /></span>}
    </>
  );

  if (onClick) {
    return <button type="button" onClick={onClick} className={className} data-testid={testId}>{content}</button>;
  }
  return <div className={className} data-testid={testId}>{content}</div>;
}

export function SectionCard({ title, description, icon: Icon, action, children, className = "", testId, compact = false }) {
  return (
    <section className={`border border-[#E0D9CD] bg-white ${compact ? "rounded-[16px] p-4 sm:p-5" : "rounded-2xl p-5 sm:p-6"} ${className}`} data-testid={testId}>
      {(title || description || Icon || action) && (
        <header className={`${compact ? "mb-4" : "mb-5"} flex items-start justify-between gap-4`}>
          <div className="flex min-w-0 items-start gap-3">
            {Icon && <span className={`grid shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B] ${compact ? "h-8 w-8" : "h-9 w-9"}`}><Icon className="h-4 w-4" /></span>}
            <div className="min-w-0">
              {title && <h2 className={`font-display font-bold ${compact ? "text-base sm:text-lg" : "text-lg sm:text-xl"}`}>{title}</h2>}
              {description && <p className="mt-1 text-xs leading-relaxed text-[#6B655E] sm:text-sm">{description}</p>}
            </div>
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function SectionLink({ to, children, testId }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 rounded-lg text-xs font-semibold text-[#F75A1B] hover:text-[#D94A12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30" data-testid={testId}>
      {children}<ArrowRight className="h-3.5 w-3.5" />
    </Link>
  );
}

export function EmptyState({ icon: Icon, title, description, compact = false }) {
  return (
    <div className={`text-center ${compact ? "py-5" : "py-8"}`}>
      {Icon && <span className="mx-auto grid h-10 w-10 place-items-center rounded-xl bg-[#F6EFE4] text-[#8A8378]"><Icon className="h-5 w-5" /></span>}
      <p className="mt-3 text-sm font-semibold text-[#1F1F1F]">{title}</p>
      {description && <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-[#6B655E]">{description}</p>}
    </div>
  );
}

export function PageLoading({ label = "Carregando…" }) {
  return (
    <div className="flex min-h-[45vh] items-center justify-center" data-testid="admin-page-loading">
      <div className="inline-flex items-center gap-2 rounded-full border border-[#E0D9CD] bg-white px-4 py-2 text-sm text-[#6B655E]">
        <Loader2 className="h-4 w-4 animate-spin text-[#F75A1B]" /> {label}
      </div>
    </div>
  );
}